import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { ErrorSource } from "./useErrorLogger";

export interface ErrorLog {
  id: string;
  restaurant_id: string | null;
  user_id: string | null;
  source: ErrorSource;
  error_code: string | null;
  error_message: string;
  error_stack: string | null;
  context: Record<string, unknown> | null;
  url: string | null;
  user_agent: string | null;
  created_at: string;
}

interface ErrorLogsFilters {
  source?: ErrorSource;
  dateFrom?: Date;
  dateTo?: Date;
  searchText?: string;
}

export function useErrorLogs() {
  const { activeRestaurant } = useRestaurantContext();
  const [logs, setLogs] = useState<ErrorLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const fetchLogs = useCallback(async (
    page: number = 1,
    pageSize: number = 50,
    filters?: ErrorLogsFilters
  ) => {
    if (!activeRestaurant?.id) return;

    setIsLoading(true);
    try {
      let query = supabase
        .from("error_logs")
        .select("*", { count: "exact" })
        .eq("restaurant_id", activeRestaurant.id)
        .order("created_at", { ascending: false });

      if (filters?.source) {
        query = query.eq("source", filters.source);
      }

      if (filters?.dateFrom) {
        query = query.gte("created_at", filters.dateFrom.toISOString());
      }

      if (filters?.dateTo) {
        const endOfDay = new Date(filters.dateTo);
        endOfDay.setHours(23, 59, 59, 999);
        query = query.lte("created_at", endOfDay.toISOString());
      }

      if (filters?.searchText) {
        query = query.or(`error_message.ilike.%${filters.searchText}%,error_code.ilike.%${filters.searchText}%`);
      }

      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;

      setLogs(data as ErrorLog[] || []);
      setTotalCount(count || 0);
    } catch (error) {
      console.error("Erro ao buscar logs de erro:", error);
    } finally {
      setIsLoading(false);
    }
  }, [activeRestaurant?.id]);

  const deleteLog = useCallback(async (logId: string) => {
    try {
      const { error } = await supabase
        .from("error_logs")
        .delete()
        .eq("id", logId);

      if (error) throw error;
      
      setLogs(prev => prev.filter(log => log.id !== logId));
      setTotalCount(prev => prev - 1);
      return true;
    } catch (error) {
      console.error("Erro ao deletar log:", error);
      return false;
    }
  }, []);

  const clearAllLogs = useCallback(async () => {
    if (!activeRestaurant?.id) return false;
    
    try {
      const { error } = await supabase
        .from("error_logs")
        .delete()
        .eq("restaurant_id", activeRestaurant.id);

      if (error) throw error;
      
      setLogs([]);
      setTotalCount(0);
      return true;
    } catch (error) {
      console.error("Erro ao limpar logs:", error);
      return false;
    }
  }, [activeRestaurant?.id]);

  return {
    logs,
    isLoading,
    totalCount,
    fetchLogs,
    deleteLog,
    clearAllLogs,
  };
}
